import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { ItineraryItem, CheckoutPayload } from '../types/bot.types';

export interface BotExtState {
  sessionId: string | null;
  sessionStatus: 'active' | 'archived' | 'blocked' | null;
  selections: ItineraryItem[];
  totalPrice: number;
  currency: string;
  checkoutReady: boolean;
  checkoutPayload: CheckoutPayload | null;
  /** Cache of `sessionId → tab label` so tab strip labels survive tab
   *  switches (the active session's first-user-message text is only in
   *  memory while that session is loaded). Populated on session load and
   *  whenever a user message lands. */
  tabTitles: Record<string, string>;
}

export type BotExtAction =
  | { type: 'SET_SESSION'; sessionId: string; status: BotExtState['sessionStatus'] }
  | { type: 'SET_SESSION_STATUS'; status: BotExtState['sessionStatus'] }
  | { type: 'SET_SELECTIONS'; selections: ItineraryItem[]; totalPrice: number; currency: string }
  | { type: 'SET_CHECKOUT'; payload: CheckoutPayload }
  /** Hide the panel but keep `checkoutPayload` so the header chip can re-open
   *  it instantly. Used by the panel's close (X) button. */
  | { type: 'HIDE_CHECKOUT' }
  /** Re-show the panel using whatever `checkoutPayload` is already in state.
   *  Used by the header "Preview cart" chip. No-op if payload is empty. */
  | { type: 'SHOW_CHECKOUT' }
  /** Discard both the panel visibility AND the payload. Used on session
   *  reset / new chat so a stale checkout doesn't bleed across sessions. */
  | { type: 'CLEAR_CHECKOUT' }
  /** Update the cached tab label for a session. Idempotent — re-setting
   *  the same title is a no-op. Setting empty string is rejected so we
   *  don't overwrite a good label with a placeholder. */
  | { type: 'SET_TAB_TITLE'; sessionId: string; title: string }
  /** Drop the cached label for a session (called when a tab closes). */
  | { type: 'CLEAR_TAB_TITLE'; sessionId: string }
  | { type: 'RESET_BOT' };

const initialBotState: BotExtState = {
  sessionId: null,
  sessionStatus: null,
  selections: [],
  totalPrice: 0,
  currency: 'USD',
  checkoutReady: false,
  checkoutPayload: null,
  tabTitles: {},
};

function botExtReducer(state: BotExtState, action: BotExtAction): BotExtState {
  switch (action.type) {
    case 'SET_SESSION':
      return { ...state, sessionId: action.sessionId, sessionStatus: action.status };
    case 'SET_SESSION_STATUS':
      return { ...state, sessionStatus: action.status };
    case 'SET_SELECTIONS':
      return { ...state, selections: action.selections, totalPrice: action.totalPrice, currency: action.currency };
    case 'SET_CHECKOUT':
      return { ...state, checkoutReady: true, checkoutPayload: action.payload };
    case 'HIDE_CHECKOUT':
      return { ...state, checkoutReady: false };
    case 'SHOW_CHECKOUT':
      // No-op if there's nothing to show; prevents the chip from opening an
      // empty panel.
      return state.checkoutPayload ? { ...state, checkoutReady: true } : state;
    case 'CLEAR_CHECKOUT':
      return { ...state, checkoutReady: false, checkoutPayload: null };
    case 'SET_TAB_TITLE': {
      const t = action.title?.trim();
      if (!t || state.tabTitles[action.sessionId] === t) return state;
      return { ...state, tabTitles: { ...state.tabTitles, [action.sessionId]: t } };
    }
    case 'CLEAR_TAB_TITLE': {
      if (!(action.sessionId in state.tabTitles)) return state;
      const next = { ...state.tabTitles };
      delete next[action.sessionId];
      return { ...state, tabTitles: next };
    }
    case 'RESET_BOT':
      // Keep tabTitles across full state resets so a tab switch (which
      // also dispatches RESET_BOT to clear sessionId / selections / etc.)
      // doesn't blow away the cached labels for OTHER tabs.
      return { ...initialBotState, tabTitles: state.tabTitles };
    default:
      return state;
  }
}

interface BotContextValue {
  botState: BotExtState;
  botDispatch: React.Dispatch<BotExtAction>;
}

const BotContext = createContext<BotContextValue | null>(null);

export const BotExtProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [botState, botDispatch] = useReducer(botExtReducer, initialBotState);
  return <BotContext.Provider value={{ botState, botDispatch }}>{children}</BotContext.Provider>;
};

export function useBotContext(): BotContextValue {
  const ctx = useContext(BotContext);
  if (!ctx) throw new Error('useBotContext must be inside BotExtProvider');
  return ctx;
}
